import { useTenant } from "@how/app-kit";
import { Card } from "@mantine/core";
import {
  IconCalendarEvent,
  IconChartLine,
  IconHome2,
  IconUserPlus,
  IconUsers,
  IconUsersGroup,
} from "@tabler/icons-react";
import { useMemo } from "react";
import { DISPLAY_LOCALE, StatusBadge } from "@how/ui";
import {
  HomeDashboard,
  type HomeDesign,
  countInto,
  greetingFor,
  lastMonths,
  runningTotal,
  type DashboardData,
} from "@how/ui/dashboard";
import { env } from "../../lib/env";
import { useOwnProfile, useTenantRecord } from "../tenant/queries";
import { ChurchTimeZone } from "./ChurchTimeZone";
import { useStaffFigures, useUpcomingOccurrences } from "./home-queries";

function formatWhen(iso: string, timeZone: string | undefined): string {
  return new Date(iso).toLocaleString(DISPLAY_LOCALE, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    ...(timeZone === undefined ? {} : { timeZone }),
  });
}

/**
 * The staff home page in the Horizon design: the greeting and shortcuts as the page header, then
 * this church's figures, who joined lately, what is on the calendar and the church's own details. The
 * figures are read through the same policies as every other screen.
 */
export function HomePage({ design = "horizon" }: { readonly design?: HomeDesign } = {}) {
  const tenant = useTenant();
  const record = useTenantRecord();
  const profile = useOwnProfile();
  // Fixed for the life of the page, so the query keys hold still.
  const clock = useMemo(() => {
    const now = new Date();
    const months = lastMonths(6, now);
    return {
      now,
      months,
      from: (months[0]?.start ?? now).toISOString(),
      until: new Date(now.getTime() + 30 * 86_400_000).toISOString(),
    };
  }, []);
  const figures = useStaffFigures(clock.from);
  const upcoming = useUpcomingOccurrences(clock.now.toISOString(), clock.until);

  const f = figures.data;
  const added =
    f?.addedSince === null || f === undefined ? null : countInto(clock.months, f.addedSince);
  const peopleSeries =
    added === null || f?.people === null || f === undefined
      ? undefined
      : runningTotal(added, f.people);
  const thisMonth = added === null ? null : (added[added.length - 1]?.value ?? 0);
  const loading = figures.isPending;
  const name = record.data?.name ?? (tenant.status === "resolved" ? tenant.tenant.slug : "");
  const timeZone = record.data?.time_zone;
  // On the staff door the church's address is the door with the church in the path, not its old dashboard host.
  const servedOn =
    tenant.status !== "resolved"
      ? "—"
      : tenant.tenant.basePath === ""
        ? tenant.tenant.hostname
        : `dash.${env.VITE_HOW_PLATFORM_BASE_DOMAIN}${tenant.tenant.basePath}/`;

  const data: DashboardData = {
    surface: "Staff dashboard",
    greeting: greetingFor(clock.now, profile.data?.display_name),
    subtitle: `Here is how ${name === "" ? "your church" : name} looks today: the people on file, who joined lately, and what is on the calendar next.`,
    metrics: [
      {
        key: "people",
        label: "People",
        value: f?.people ?? null,
        loading,
        hint: "On file now",
        icon: IconUsers,
        tone: "sky",
        ...(peopleSeries === undefined ? {} : { series: peopleSeries }),
        to: "/people",
      },
      {
        key: "new",
        label: "Added this month",
        value: thisMonth,
        loading,
        hint: "New people since the 1st",
        icon: IconUserPlus,
        tone: "mint",
        ...(added === null ? {} : { series: added.map((p) => p.value) }),
      },
      {
        key: "households",
        label: "Households",
        value: f?.households ?? null,
        loading,
        hint: "Families and homes",
        icon: IconHome2,
        tone: "iris",
        to: "/people/households",
      },
      {
        key: "events",
        label: "Dates ahead",
        value: upcoming.isError ? null : (upcoming.data?.total ?? null),
        loading: upcoming.isPending,
        hint: "Next 30 days",
        icon: IconCalendarEvent,
        tone: "amber",
        to: "/events",
      },
    ],
    trend:
      f?.addedSince === null
        ? null
        : {
            title: "People added",
            caption: "New people each month, the last six months",
            points: added ?? [],
            loading,
            unit: "People added",
          },
    breakdown:
      f !== undefined && f.campuses === null
        ? null
        : {
            title: "People by campus",
            caption: "Where each person calls home",
            parts: (f?.campuses ?? []).map((c) => ({ label: c.name, value: c.people })),
            loading,
            totalLabel: "people",
          },
    list: {
      title: "Coming up",
      caption: "The next dates on the calendar",
      loading: upcoming.isPending,
      empty: upcoming.isError
        ? "The calendar is not available to you."
        : "Nothing on the calendar in the next 30 days.",
      items: (upcoming.data?.next ?? []).map((o) => ({
        id: o.id,
        title: o.title,
        ...(o.location === null ? {} : { meta: o.location }),
        when: formatWhen(o.startsAt, timeZone),
      })),
      more: { label: "All events", to: "/events" },
    },
    facts: {
      title: "Your church",
      rows: [
        { label: "Name", value: name },
        {
          label: "Status",
          value:
            record.data === undefined ? (
              "—"
            ) : (
              <StatusBadge domain="tenant_status" value={record.data.status} />
            ),
        },
        {
          label: "Active groups",
          value:
            f?.activeGroups === null || f === undefined
              ? "—"
              : f.activeGroups.toLocaleString(DISPLAY_LOCALE),
        },
        { label: "Served on", value: servedOn },
      ],
    },
    // The church's time zone stays on Home, where every staff member can read it (owner, 2026-10-05).
    ...(timeZone === undefined
      ? {}
      : {
          extra: (
            <Card>
              <ChurchTimeZone zone={timeZone} />
            </Card>
          ),
        }),
    actions: [
      { label: "People", to: "/people", icon: IconUsers },
      { label: "Events", to: "/events", icon: IconCalendarEvent },
      { label: "Groups", to: "/groups", icon: IconUsersGroup },
      { label: "Reports", to: "/reports", icon: IconChartLine },
    ],
  };

  return <HomeDashboard data={data} design={design} />;
}
